import React, { forwardRef, useImperativeHandle, useRef, useEffect } from "react";
import { Autocomplete, TextField, Typography, Box } from "@mui/material";
import "../../../common/commonStyles.css";

const getCustomerAccountNo = (option) => {
  if (!option) return "";
  const raw =
    option.accound_no ??
    option.account_no ??
    option.accountNo ??
    option.account_number ??
    "";
  return String(raw).trim();
};

export const formatCustomerDisplayLabel = (option) => {
  if (!option) return "";
  const name = String(option.company || option.name || "").trim();
  const accountNo = getCustomerAccountNo(option);
  if (!name) return accountNo;
  if (!accountNo) return name;
  return `${name} (${accountNo})`;
};

const CustomerSearch = forwardRef(({ 
  selectedCustomer, 
  onCustomerChange, 
  searchInput, 
  onSearchInputChange, 
  options, 
  isLoading,
  debouncedSearch,
  onEnterKey,
  onTabKey,
  disabled = false,
  disabledTextColor
}, ref) => {
  const inputRef = useRef(null);
  const highlightedOptionRef = useRef(null);

  useImperativeHandle(ref, () => ({
    focusInput: () => {
      if (inputRef.current) {
        inputRef.current.focus();
      }
    }
  }));

  const handleKeyDown = (event) => {
    if (event.key === 'Tab' && highlightedOptionRef.current) {
      const nextCustomer = highlightedOptionRef.current;
      event.preventDefault();
      onCustomerChange(nextCustomer);
      onSearchInputChange(formatCustomerDisplayLabel(nextCustomer));
      if (onTabKey) {
        setTimeout(() => onTabKey(nextCustomer), 0);
      }
      return;
    }
    if (event.key === 'Enter' && selectedCustomer && onEnterKey) {
      event.preventDefault();
      onEnterKey();
    }
  };

  const handleFocus = (event) => {
    const el = event.target;
    if (el && typeof el.select === 'function') el.select();
  };

  const handleClick = () => {
    if (inputRef.current && typeof inputRef.current.select === 'function') {
      inputRef.current.select();
    }
  };

  useEffect(() => {
    if (!selectedCustomer) return;
    const fullLabel = formatCustomerDisplayLabel(selectedCustomer);
    const nameOnly = String(selectedCustomer.company || selectedCustomer.name || "").trim();
    if (fullLabel && (searchInput === nameOnly || searchInput === "")) {
      onSearchInputChange(fullLabel);
    }
  }, [selectedCustomer, searchInput, onSearchInputChange]);

  return (
    <Autocomplete
      fullWidth
      disabled={disabled}
      openOnFocus
      value={selectedCustomer}
      onChange={(event, newValue) => {
        onCustomerChange(newValue);
        if (newValue) {
          onSearchInputChange(formatCustomerDisplayLabel(newValue));
        } else {
          onSearchInputChange("");
        }
      }}
      inputValue={searchInput}
      onInputChange={(event, newInputValue) => onSearchInputChange(newInputValue)}
      options={options}
      getOptionLabel={(option) => formatCustomerDisplayLabel(option)}
      filterOptions={(x) => x}
      loading={isLoading}
      noOptionsText={debouncedSearch.length < 3 ? "Type at least 3 characters" : "No customers found"}
      onHighlightChange={(event, option) => {
        highlightedOptionRef.current = option || null;
      }}
      renderInput={(params) => (
        <TextField
          className="input-border"
          {...params}
          inputRef={inputRef}
          placeholder="Enter Company Name"
          onKeyDown={handleKeyDown}
          onFocus={handleFocus}
     
          onClick={handleClick}
          sx={{ 
            '& .MuiInputBase-root': { height: '35px' },
            '& .MuiFormLabel-root': { top: '-8px' },
            '& .MuiInputLabel-shrink': { top: '0 !important' },
            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: "#000",
            },
            "& .MuiAutocomplete-input": {
              fontWeight: selectedCustomer ? 700 : 400,
            },
            ...(disabledTextColor ? {
              "& .MuiInputBase-input.Mui-disabled": {
                color: disabledTextColor,
                WebkitTextFillColor: disabledTextColor,
                opacity: 1,
              },
            } : {}),
          }}
          margin="none"
        />
      )}
      renderOption={(props, option) => (
        <li {...props} key={option.id}>
          <Box>
            <Typography variant="body2">{formatCustomerDisplayLabel(option)}</Typography>
            {/* <Typography variant="caption" color="text.secondary">
              {option.name} - {option.accound_no}
            </Typography> */}
          </Box>
        </li>
      )}
    />
  );
});

CustomerSearch.displayName = 'CustomerSearch';

export default CustomerSearch;

